const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'libs/common_web/src/components/PDFViewer/usePDFDocumentController.ts';
import { MutableRefObject, useCallback, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTheme } from '@mui/material/styles';
import textMap from './translations';
import { showSnackbar } from '../../actions/uiActions';
import { KeyValuePair } from '../../defs/generalDefs';
import { downloadBase64PDF } from '../../utils/pdf';
import { MIN_PDF_SCALE_PERCENT, MAX_PDF_SCALE_PERCENT } from './pdfZoomUtils';

interface Options {
    documentRef: MutableRefObject<any> | null;
    documentTitle?: string;
}

/**
 * All state, document plumbing, and actions shared by the toolbar and
 * floating variants of the PDF document controller.
 */
export function usePDFDocumentController({ documentRef, documentTitle }: Options) {
    const theme = useTheme();
    const dispatch = useDispatch();

    const [changed, setChanged] = useState(false);
    const [disabled, setDisabled] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    // Drawing state — kept in sync with PDFDocument via setDocumentState
    const [drawingEnabled, setDrawingEnabled] = useState(false);
    const [hasActiveDrawing, setHasActiveDrawing] = useState(false);
    const [pageCount, setPageCount] = useState(1);
    const [pageNumber, setPageNumber] = useState(1);
    const [pdfASCIIString, setPDFASCIIString] = useState('');
    const [scale, setScale] = useState(100);

    const drawingHintShownRef = useRef(false);

    const setDocumentChanged = useCallback((changed: boolean) => setChanged(changed), []);

    const setDocumentState = useCallback((data: KeyValuePair) => {
        setDisabled(!Boolean(data.pdfASCIIString));
        setIsSaving(data.isSaving);
        setDrawingEnabled(data.enableDrawing);
        setHasActiveDrawing(data.hasActiveDrawing);
        setPageCount(data.pageCount);
        setPageNumber(data.pageNumber);
        setPDFASCIIString(data.pdfASCIIString);
        setScale(data.scale);
    }, []);

    const toggleDocumentEditMode = () => {
        if (!drawingEnabled && !drawingHintShownRef.current) {
            drawingHintShownRef.current = true;
            dispatch(
                showSnackbar(textMap.pinchZoomDisabledInDrawingMode, {
                    uiOpts: {
                        anchorOrigin: { vertical: 'bottom', horizontal: 'center' },
                        autoHideDuration: 2500,
                        sx: { mb: 5 },
                    },
                })
            );
        }
        drawingEnabled ? documentRef?.current?.disableDrawing() : documentRef?.current?.enableDrawing();
    };

    const save = () => documentRef?.current?.save();

    /**
     * Single place that decides what saving means. Previously only the
     * compact variant exited drawing mode on save; now both variants do.
     * If the variants genuinely must differ, expose `exitDrawing`
     * separately instead of re-forking this.
     */
    const saveAndExitDrawing = () => {
        save();
        documentRef?.current?.disableDrawing();
    };

    const undo = () => documentRef?.current?.undo();

    const startDownload = () => downloadBase64PDF(pdfASCIIString, `${documentTitle}.pdf`);

    const offsetDocumentPage = (amount: number) => {
        const newPageNumber = pageNumber + amount;
        if (newPageNumber < 1 || newPageNumber > pageCount) {
            return;
        }
        documentRef?.current?.setPage(newPageNumber);
    };

    const showPreviousPage = () => offsetDocumentPage(-1);
    const showNextPage = () => offsetDocumentPage(1);

    const setDocumentScale = (scale: number) => documentRef?.current?.setScale(scale);
    const zoomIn = () => setDocumentScale(scale + 25);
    const zoomOut = () => setDocumentScale(scale - 25);

    return {
        // state
        changed,
        disabled,
        isSaving,
        drawingEnabled,
        hasActiveDrawing,
        pageCount,
        pageNumber,
        scale,
        canZoomIn: scale < MAX_PDF_SCALE_PERCENT,
        canZoomOut: scale > MIN_PDF_SCALE_PERCENT,
        canSave: changed || hasActiveDrawing,
        // actions
        toggleDocumentEditMode,
        save,
        saveAndExitDrawing,
        undo,
        startDownload,
        showPreviousPage,
        showNextPage,
        zoomIn,
        zoomOut,
        setDocumentChanged,
        setDocumentState,
    };
}
